<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConversationLabels from './ConversationLabels.vue';
import LeadOriginCard from 'dashboard/components-next/LeadOrigin/LeadOriginCard.vue';
import KanbanContactPhoneNumbers from './KanbanContactPhoneNumbers.vue';
import KanbanScheduleMessageForm from './KanbanScheduleMessageForm.vue';
import TasksList from 'dashboard/components/widgets/conversation/TasksList.vue';
import ScheduledMessagesList from 'dashboard/components/widgets/conversation/ScheduledMessagesList.vue';
import ContactNotes from 'dashboard/routes/dashboard/conversation/contact/ContactNotes.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { relativeDayTimestamp } from 'shared/helpers/timeHelper';

const { t } = useI18n();
const store = useStore();
const { accountScopedUrl } = useAccount();
const { getPlainText } = useMessageFormatter();

const emit = defineEmits(['note-added']);

const dialogRef = ref(null);
// Bio/country/social profiles (via ContactsForm's `show-extra-fields` prop)
// and Notes are tucked behind a single "more fields" toggle to keep the
// lightbox scannable — only name/email/phone/company stay always visible.
const moreFieldsOpen = ref(false);
// The conversation behind the card that was clicked. A "kanban card" IS a
// Conversation (has kanban_stage_id) — it isn't a separate entity — so Tasks,
// ScheduledMessages, Labels, and the private note (all scoped by
// conversation_id) key off `conversation.id`, while contact Notes/edit
// (scoped by contact_id) key off `conversation.meta.sender.id`.
const activeConversation = ref(null);
const formState = ref({});

const conversationId = computed(() => activeConversation.value?.id ?? null);
const contactId = computed(
  () => activeConversation.value?.meta?.sender?.id ?? null
);
// See app/javascript/dashboard/components-next/LeadOrigin/LeadOriginCard.vue —
// populated from the WhatsApp Cloud webhook referral (Click-to-WhatsApp/Instagram ads).
const conversationAdditionalAttributes = computed(
  () => activeConversation.value?.additional_attributes || {}
);

const getContactById = useMapGetter('contacts/getContactById');
const contactUiFlags = useMapGetter('contacts/getUIFlags');
const isSavingContact = computed(() => contactUiFlags.value.isUpdating);
const currentUser = useMapGetter('getCurrentUser');

// `conversation.meta.sender` already carries the full denormalized contact
// snapshot (id, name, email, phone_number, additional_attributes, thumbnail) —
// see app/views/api/v1/models/_contact.json.jbuilder — so the lightbox opens
// instantly with real data, no loading flash while `contacts/show` revalidates
// in the background.
const fallbackContact = computed(() => {
  const sender = activeConversation.value?.meta?.sender || {};
  return {
    id: sender.id,
    name: sender.name || '',
    email: sender.email || '',
    phoneNumber: sender.phone_number || '',
    thumbnail: sender.thumbnail,
    additionalAttributes: sender.additional_attributes || {},
  };
});

const fetchedContact = computed(() =>
  contactId.value ? getContactById.value(contactId.value) : {}
);

// Live, store-backed contact — used by children that dispatch their own writes
// (labels, phone numbers) so they reflect updates immediately without the
// lightbox needing to know about it.
const contact = computed(() =>
  fetchedContact.value?.id ? fetchedContact.value : fallbackContact.value
);

// Private note composer — posts a real private message on the conversation
// (message_type outgoing, private: true), the same mechanism the main
// conversation reply box uses for "Private note" replies, so it shows up in
// that conversation's message history for any agent, not just a kanban-only
// note. See ReplyBox.vue#sendMessage / store `createPendingMessageAndSend`.
const noteText = ref('');
const isSendingNote = ref(false);
const privateNotes = ref([]);

const open = (conversation, notes = []) => {
  activeConversation.value = conversation;
  privateNotes.value = Array.isArray(notes) ? [...notes] : [];
  formState.value = { ...fallbackContact.value };
  moreFieldsOpen.value = false;
  noteText.value = '';
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
  activeConversation.value = null;
  noteText.value = '';
  privateNotes.value = [];
};

// ContactsForm rebuilds `additionalAttributes` from only the fields it knows
// about (description/companyName/countryCode/country/city/socialProfiles) — it
// has no notion of `phoneNumbers`. A naive Object.assign here would wipe the
// extra phone numbers out of the local edit buffer until the next refetch, so
// the merge is explicit at the additionalAttributes level.
const handleFormUpdate = updatedData => {
  formState.value = {
    ...formState.value,
    ...updatedData,
    additionalAttributes: {
      ...formState.value.additionalAttributes,
      ...updatedData.additionalAttributes,
    },
  };
};

const saveContact = async () => {
  try {
    await store.dispatch('contacts/update', { ...formState.value });
    useAlert(t('KANBAN.CONTACT_LIGHTBOX.CONTACT_INFO.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('KANBAN.CONTACT_LIGHTBOX.CONTACT_INFO.ERROR_MESSAGE'));
  }
};

// Mutates the same reactive conversation object KanbanCard renders (passed
// through unchanged from KanbanView's local board state), so the card's
// label pills update immediately without a board refetch.
const handleLabelsUpdated = labels => {
  if (activeConversation.value) {
    activeConversation.value.labels = labels;
  }
};

const sendPrivateNote = async () => {
  const content = noteText.value.trim();
  if (!content || !conversationId.value) return;

  isSendingNote.value = true;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: conversationId.value,
      message: content,
      private: true,
    });
    const newNote = {
      id: Date.now(),
      content,
      created_at: Math.floor(Date.now() / 1000),
      sender_name: currentUser.value?.name || '',
    };
    privateNotes.value = [newNote, ...privateNotes.value].slice(0, 3);
    emit('note-added', {
      conversationId: conversationId.value,
      note: newNote,
    });
    noteText.value = '';
    useAlert(t('KANBAN.CONTACT_LIGHTBOX.PRIVATE_NOTE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('KANBAN.CONTACT_LIGHTBOX.PRIVATE_NOTE.ERROR_MESSAGE'));
  } finally {
    isSendingNote.value = false;
  }
};

const openFullConversation = () => {
  if (!conversationId.value) return;
  window.open(accountScopedUrl(`conversations/${conversationId.value}`), '_blank');
};

defineExpose({ open, close });
</script>

<template>
  <Dialog ref="dialogRef" type="edit" width="2xl" :show-cancel-button="false" :show-confirm-button="false">
    <template #default>
      <!-- Full-bleed chrome: negative margin cancels Dialog's own p-6 so the
      header/profile bars can run edge-to-edge with their own borders, while
      only the middle section scrolls (header/profile/footer stay put). -->
      <div
        v-if="contactId"
        class="flex flex-col -m-6 max-h-[85vh] overflow-hidden rounded-xl"
      >
        <header
          class="flex items-center justify-between gap-3 px-6 h-16 shrink-0 border-b border-n-weak"
        >
          <h2 class="text-base font-bold truncate text-n-ruby-9 dark:text-n-ruby-9">
            {{ contact.name }}
          </h2>
          <div class="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="faded"
              color="slate"
              icon="i-lucide-external-link"
              :label="t('KANBAN.CONTACT_LIGHTBOX.OPEN_FULL_CONVERSATION')"
              @click="openFullConversation"
            />
            <Button
              ghost
              slate
              sm
              icon="i-lucide-x"
              :aria-label="t('KANBAN.CONTACT_LIGHTBOX.CLOSE')"
              @click="close"
            />
          </div>
        </header>

        <div
          class="flex items-center gap-4 px-6 py-4 shrink-0 border-b border-n-weak bg-n-alpha-1"
        >
          <Avatar :src="contact.thumbnail" :name="contact.name || ''" :size="48" />
          <div class="flex flex-col min-w-0">
            <h3 class="text-sm font-semibold truncate text-n-slate-12">
              {{ contact.name }}
            </h3>
            <p class="text-xs truncate text-n-slate-11">
              {{ contact.phoneNumber || contact.email }}
            </p>
          </div>
        </div>

        <div class="flex flex-col flex-1 min-h-0 gap-6 px-6 py-6 overflow-y-auto">
          <LeadOriginCard :additional-attributes="conversationAdditionalAttributes" />

          <section class="flex flex-col items-start gap-3">
            <ContactsForm
              :contact-data="formState"
              :show-extra-fields="moreFieldsOpen"
              is-details-view
              @update="handleFormUpdate"
            />
            <Button
              size="sm"
              :label="t('KANBAN.CONTACT_LIGHTBOX.CONTACT_INFO.SAVE')"
              :is-loading="isSavingContact"
              :disabled="isSavingContact"
              @click="saveContact"
            />
          </section>

          <section v-if="moreFieldsOpen" class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('KANBAN.CONTACT_LIGHTBOX.NOTES.TITLE') }}
            </span>
            <ContactNotes :contact-id="contactId" />
          </section>

          <hr class="border-n-weak">

          <section>
            <KanbanContactPhoneNumbers :contact="contact" />
          </section>

          <hr class="border-n-weak">

          <section class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('KANBAN.CONTACT_LIGHTBOX.LABELS.TITLE') }}
            </span>
            <ConversationLabels
              :conversation-id="conversationId"
              @updated="handleLabelsUpdated"
            />
          </section>

          <hr class="border-n-weak">

          <section class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.CONTACT_LIGHTBOX.PRIVATE_NOTE.TITLE') }}
              </span>
              <button
                v-if="conversationId"
                type="button"
                class="flex items-center gap-1 text-xs font-medium text-n-ruby-11 hover:underline"
                @click="openFullConversation"
              >
                <span>{{ t('KANBAN.CONTACT_LIGHTBOX.PRIVATE_NOTE.VIEW_ALL') }}</span>
                <Icon icon="i-lucide-arrow-up-right" class="size-3" />
              </button>
            </div>

            <div v-if="privateNotes.length" class="flex flex-col gap-2">
              <div
                v-for="note in privateNotes"
                :key="note.id"
                class="p-2.5 rounded-lg bg-n-solid-2 border border-n-weak flex flex-col gap-1"
              >
                <div class="flex items-center justify-between text-[11px] text-n-slate-10">
                  <span v-if="note.sender_name" class="font-semibold text-n-slate-12">
                    {{ note.sender_name }}
                  </span>
                  <span>{{ relativeDayTimestamp(note.created_at) }}</span>
                </div>
                <p class="text-xs text-n-slate-12 whitespace-pre-wrap leading-relaxed">
                  {{ getPlainText(note.content) }}
                </p>
              </div>
            </div>

            <TextArea
              v-model="noteText"
              auto-height
              min-height="4.5rem"
              :placeholder="t('KANBAN.CONTACT_LIGHTBOX.PRIVATE_NOTE.PLACEHOLDER')"
              :disabled="isSendingNote"
            />
            <Button
              size="sm"
              :label="t('KANBAN.CONTACT_LIGHTBOX.PRIVATE_NOTE.SEND')"
              class="self-end"
              :is-loading="isSendingNote"
              :disabled="!noteText.trim() || isSendingNote"
              @click="sendPrivateNote"
            />
          </section>

          <hr class="border-n-weak">

          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <section class="flex flex-col gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.CONTACT_LIGHTBOX.TASKS.TITLE') }}
              </span>
              <TasksList
                v-if="conversationId"
                :conversation-id="conversationId"
                class="!px-0"
              />
            </section>

            <section class="flex flex-col gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.CONTACT_LIGHTBOX.SCHEDULED_MESSAGES.TITLE') }}
              </span>
              <ScheduledMessagesList
                v-if="conversationId"
                :conversation-id="conversationId"
                class="!px-0"
              />
              <KanbanScheduleMessageForm
                v-if="conversationId"
                :conversation-id="conversationId"
              />
            </section>
          </div>
        </div>

        <footer
          class="flex items-center justify-between gap-3 px-6 py-4 shrink-0 border-t border-n-weak"
        >
          <Button
            size="sm"
            variant="ghost"
            :icon="moreFieldsOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
            trailing-icon
            :label="t('KANBAN.CONTACT_LIGHTBOX.MORE_FIELDS')"
            @click="moreFieldsOpen = !moreFieldsOpen"
          />
          <Button
            variant="faded"
            color="slate"
            :label="t('KANBAN.CONTACT_LIGHTBOX.CLOSE')"
            @click="close"
          />
        </footer>
      </div>
    </template>
  </Dialog>
</template>
